import type { MissionInterface } from "./mission.service";

export const MISSION_POPUP_TTL_MS = 60_000;

export interface MissionPopupMemory {
  seen: ReadonlyMap<number, number>;
  dismissed: ReadonlySet<number>;
}

export const EMPTY_POPUP_MEMORY: MissionPopupMemory = {
  seen: new Map(),
  dismissed: new Set(),
};

export function missionPopups<T extends { id: number }>(
  open: readonly T[],
  memory: MissionPopupMemory,
  now: number,
  ttlMs: number = MISSION_POPUP_TTL_MS
): { visible: T[]; memory: MissionPopupMemory } {
  const openIds = new Set(open.map((m) => m.id));
  const seen = new Map([...memory.seen].filter(([id]) => openIds.has(id)));
  const dismissed = new Set([...memory.dismissed].filter((id) => openIds.has(id)));

  const visible: T[] = [];
  for (const mission of open) {
    if (!seen.has(mission.id)) {
      seen.set(mission.id, now);
    }
    const firstSeen = seen.get(mission.id)!;
    if (!dismissed.has(mission.id) && now - firstSeen < ttlMs) {
      visible.push(mission);
    }
  }

  return { visible, memory: { seen, dismissed } };
}

export function dismissMissionPopup(memory: MissionPopupMemory, id: number): MissionPopupMemory {
  const dismissed = new Set(memory.dismissed);
  dismissed.add(id);
  return { seen: new Map(memory.seen), dismissed };
}

export function parseMissionSnapshot(data: unknown): MissionInterface[] | null {
  if (typeof data !== "string") {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }

  if (typeof parsed !== "object" || parsed === null || !("missions" in parsed)) {
    return null;
  }
  const rawMissions = (parsed as { missions: unknown }).missions;
  if (!Array.isArray(rawMissions)) {
    return null;
  }

  const missions: MissionInterface[] = [];
  for (const raw of rawMissions) {
    if (typeof raw !== "object" || raw === null) {
      continue;
    }
    const rec = raw as Record<string, unknown>;
    const id = Number(rec["id"]);
    if (!Number.isFinite(id)) {
      continue;
    }
    if (typeof rec["timestamp"] !== "string") {
      continue;
    }
    missions.push({
      id,
      timestamp: rec["timestamp"],
      body: typeof rec["body"] === "string" ? rec["body"] : "",
      destination: typeof rec["destination"] === "string" ? rec["destination"] : "",
      state: typeof rec["state"] === "string" ? rec["state"] : "",
      source: typeof rec["source"] === "string" ? rec["source"] : "",
    });
  }

  missions.sort((a, b) => a.timestamp.localeCompare(b.timestamp) || a.id - b.id);

  return missions;
}

// One plain-text line from the mission body, which is markdown from the
// alarming mail. Angular interpolation escapes it; nothing here renders HTML.
export function missionSummary(body: string, max = 140): string {
  const text = (body ?? "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/[#*|_`>]+/g, " ")
    .replace(/-{3,}/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > max ? text.slice(0, max - 1) + "…" : text;
}

// Only a mission this young is auto-accepted. The window can load, or reload,
// while old missions are still open, and a flight created for one of those
// would surprise the crew.
export const AUTO_ACCEPT_MAX_AGE_MS = 10 * 60_000;

// `since` is the browser's clock and a mission timestamp is the alarming
// server's. Without slack a browser clock that runs ahead would skip a fresh
// mission for good. ponytail: fixed slack, a server-side cutoff if skew grows.
export const AUTO_ACCEPT_CLOCK_SLACK_MS = 2 * 60_000;

/** The open mission the auto-accept window takes next, or undefined. `tried`
 *  holds every id already handed out, so each mission is tried once. A mission
 *  older than `since` (the time the switch went on), less the clock slack, is
 *  skipped. A timestamp that does not parse is never taken. */
export function nextAutoAccept<T extends { id: number; timestamp: string }>(
  open: readonly T[],
  tried: ReadonlySet<number>,
  now: number,
  since = 0,
  maxAgeMs: number = AUTO_ACCEPT_MAX_AGE_MS
): T | undefined {
  return open.find((m) => {
    const ts = Date.parse(m.timestamp);
    return !tried.has(m.id) && ts >= since - AUTO_ACCEPT_CLOCK_SLACK_MS && now - ts < maxAgeMs;
  });
}

// The only screens an auto-accept navigates away from. Every other screen can
// hold unsaved input, so the auto-accept waits there.
const AUTO_ACCEPT_IDLE_ROUTES = ["/flow-briefing", "/map"];

/** True for a route an auto-accept may navigate away from (query string ignored). */
export function isAutoAcceptIdleRoute(url: string): boolean {
  const path = url.split("?")[0];
  return AUTO_ACCEPT_IDLE_ROUTES.some((r) => path === r || path.startsWith(r + "/"));
}

export const AUTO_ACCEPT_OFF_KEY = "efb.autoAcceptOff";

/** Auto-accept is on unless the switch was stored as off. A failing read means on. */
export function readAutoAcceptOn(storage: Pick<Storage, "getItem"> | undefined): boolean {
  try {
    return storage?.getItem(AUTO_ACCEPT_OFF_KEY) !== "1";
  } catch {
    return true;
  }
}

/** A new set with every open mission marked as tried, so switching on creates
 *  flights only for missions that arrive later. */
export function skipOpenMissions(open: readonly { id: number }[], tried: ReadonlySet<number>): Set<number> {
  const result = new Set(tried);
  for (const m of open) result.add(m.id);
  return result;
}
